import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, ArrowRight, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import EntityPicker from "../components/EntityPicker";
import { toast } from "sonner";
import { adminApi } from "../api";

export default function AdminCopyProduct() {
  const [srcId, setSrcId] = useState("");
  const [srcLabel, setSrcLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const canSubmit = srcId && !submitting;

  const doSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setErr(""); setResult(null);
    try {
      const r = await adminApi.opsCopyProduct({
        src_id: Number(srcId),
        new_descricao: newDesc.trim(),
      });
      setResult(r);
      setConfirmOpen(false);
    } catch (e) {
      const msg = e.message || "Erro";
      setErr(msg);
      toast.error(msg, { description: "Copiar Produto" });
      setConfirmOpen(false);
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="admin-copy-product" className="max-w-3xl">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={13} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Copy size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Copiar Produto</h1>
          <p className="text-sm text-[#4B5563]">Duplica o produto e as suas campanhas, ficheiros, cursos, áreas, comissões e ofertas.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E6E1D6] rounded-md p-5 space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">Produto original</label>
          <div className="mt-1.5">
            <EntityPicker entity="produto" value={srcId}
              onChange={setSrcId} onPick={(o) => setSrcLabel(o.label || "")}
              placeholder="Referência ou descrição..." testid="picker-product" />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">Nova descrição (opcional)</label>
          <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
            data-testid="input-new-desc"
            placeholder={srcLabel ? `Cópia de ${srcLabel}` : "Deixa vazio para manter a descrição original..."}
            className="mt-1.5 w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829]" />
          <p className="mt-1 text-[11px] text-[#6B7280]">A referência do produto novo será automaticamente sufixada com «-CP».</p>
        </div>

        {err && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{err}</span>
          </div>
        )}

        {result && (
          <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 rounded" data-testid="copy-product-success">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
              <div>
                <p>Produto duplicado com sucesso. Novo ID: <Link to={`/admin/t/produto/Tinf_Produto/view/${result.new_id}`} className="font-mono underline">#{result.new_id}</Link></p>
                <ul className="mt-1 text-xs space-y-0.5">
                  {Object.entries(result.cloned_children || {}).map(([t, n]) => {
                    // `cloned_children` é um dict { tabela: numero } excepto para
                    // o caso especial `curso` (quando o produto origem tinha
                    // conteúdo de curso) que vem como sub-dict
                    // { curso, modulos, sessoes, ficheiros, avaliacoes, certificados }.
                    // Tratamos os dois casos para evitar o "Objects are not valid
                    // as a React child" que dava ecrã em branco.
                    if (n && typeof n === "object") {
                      const parts = Object.entries(n).map(([k, v]) => `${k}: ${v}`).join(", ");
                      return <li key={t}><span className="font-mono">{t}</span>: {parts}</li>;
                    }
                    return <li key={t}><span className="font-mono">{t}</span>: {n} registos</li>;
                  })}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={() => setConfirmOpen(true)} disabled={!canSubmit}
            data-testid="submit-copy-product"
            className="px-4 py-2 bg-[#0F1829] text-white text-sm rounded disabled:opacity-40 hover:bg-black transition inline-flex items-center gap-2">
            Duplicar produto <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => !submitting && setConfirmOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl w-full max-w-xl p-5" data-testid="confirm-modal">
            <div className="flex items-center gap-2 mb-3">
              <Copy size={18} className="text-[#0F1829]" />
              <h2 className="text-lg font-bold">Confirmar duplicação</h2>
            </div>
            <div className="text-sm space-y-2 bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3 mb-4">
              <div><strong>Produto original:</strong> #{srcId} · {srcLabel}</div>
              <div><strong>Nova descrição:</strong> {newDesc || <em className="text-[#6B7280]">(igual ao original)</em>}</div>
            </div>
            <p className="text-xs text-[#6B7280] mb-4">Vão ser criados registos novos em <span className="font-mono">produto.Tinf_Produto</span> e tabelas relacionadas. Esta acção fica registada em auditoria.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} disabled={submitting}
                className="px-4 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB]">Cancelar</button>
              <button onClick={doSubmit} disabled={submitting} data-testid="confirm-submit"
                className="px-4 py-2 bg-[#0F1829] text-white text-sm rounded disabled:opacity-40 inline-flex items-center gap-2">
                {submitting && <Loader2 size={14} className="animate-spin" />} Duplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
